/**
 * Utility functions for extracting and managing client currency information
 */

import { ClientOption } from '@/home/application/types/HomeTypes';

/**
 * Currency mapping based on culture info names
 * Maps culture codes to their corresponding currency codes
 */
const CULTURE_TO_CURRENCY_MAP: Record<string, string> = {
  'en-US': 'USD', // United States Dollar
  'en-CA': 'CAD', // Canadian Dollar
  'en-GB': 'GBP', // British Pound
  'es-ES': 'EUR', // Euro (Spain)
  'fr-FR': 'EUR', // Euro (France)
  'de-DE': 'EUR', // Euro (Germany)
  'it-IT': 'EUR', // Euro (Italy)
  'pt-BR': 'BRL', // Brazilian Real
  'es-MX': 'MXN', // Mexican Peso
  'ja-JP': 'JPY', // Japanese Yen
  'ko-KR': 'KRW', // South Korean Won
  'zh-CN': 'CNY', // Chinese Yuan
  'zh-TW': 'TWD', // Taiwan Dollar
  'ru-RU': 'RUB', // Russian Ruble
  'ar-SA': 'SAR', // Saudi Riyal
  'hi-IN': 'INR', // Indian Rupee
  'th-TH': 'THB', // Thai Baht
  'vi-VN': 'VND', // Vietnamese Dong
  'id-ID': 'IDR', // Indonesian Rupiah
  'ms-MY': 'MYR', // Malaysian Ringgit
  'tl-PH': 'PHP', // Philippine Peso
  'en-AU': 'AUD', // Australian Dollar
  'en-NZ': 'NZD', // New Zealand Dollar
  'af-ZA': 'ZAR', // South African Rand
  'he-IL': 'ILS', // Israeli Shekel
  'tr-TR': 'TRY', // Turkish Lira
  'pl-PL': 'PLN', // Polish Złoty
  'cs-CZ': 'CZK', // Czech Koruna
  'hu-HU': 'HUF', // Hungarian Forint
  'ro-RO': 'RON', // Romanian Leu
  'bg-BG': 'BGN', // Bulgarian Lev
  'hr-HR': 'HRK', // Croatian Kuna
  'sk-SK': 'EUR', // Euro (Slovakia)
  'sl-SI': 'EUR', // Euro (Slovenia)
  'et-EE': 'EUR', // Euro (Estonia)
  'lv-LV': 'EUR', // Euro (Latvia)
  'lt-LT': 'EUR', // Euro (Lithuania)
  'fi-FI': 'EUR', // Euro (Finland)
  'sv-SE': 'SEK', // Swedish Krona
  'da-DK': 'DKK', // Danish Krone
  'no-NO': 'NOK', // Norwegian Krone
  'is-IS': 'ISK', // Icelandic Króna
  'nl-NL': 'EUR', // Euro (Netherlands)
  'be-BY': 'BYN', // Belarusian Ruble
  'uk-UA': 'UAH', // Ukrainian Hryvnia
  'ka-GE': 'GEL', // Georgian Lari
  'hy-AM': 'AMD', // Armenian Dram
  'az-AZ': 'AZN', // Azerbaijani Manat
  'kk-KZ': 'KZT', // Kazakhstani Tenge
  'ky-KG': 'KGS', // Kyrgyzstani Som
  'tg-TJ': 'TJS', // Tajikistani Somoni
  'tk-TM': 'TMT', // Turkmenistani Manat
  'uz-UZ': 'UZS', // Uzbekistani Som
  'mn-MN': 'MNT', // Mongolian Tögrög
};

/**
 * Default currency code when no specific culture is found
 */
const DEFAULT_CURRENCY = 'USD';

/**
 * Extract currency code from client's culture information
 * @param client The client object containing culture information
 * @returns Currency code (e.g., 'USD', 'EUR', 'CAD') or default 'USD'
 */
export const getClientCurrency = (client?: ClientOption | null): string => {
  if (!client) {
    console.warn('No client provided, using default currency:', DEFAULT_CURRENCY);
    return DEFAULT_CURRENCY;
  }

  // Check if client has culture info
  if (!client.cultureInfoName) {
    console.warn(`Client ${client.name} has no culture info, using default currency:`, DEFAULT_CURRENCY);
    return DEFAULT_CURRENCY;
  }

  // Extract currency from culture mapping
  const currency = CULTURE_TO_CURRENCY_MAP[client.cultureInfoName];

  if (currency) {
    console.log(`Client ${client.name} currency determined as ${currency} from culture ${client.cultureInfoName}`);
    return currency;
  }

  // Fallback: try to extract from the culture code pattern
  const currencyFromPattern = extractCurrencyFromCulturePattern(client.cultureInfoName);
  if (currencyFromPattern) {
    console.log(`Client ${client.name} currency extracted from pattern: ${currencyFromPattern} from culture ${client.cultureInfoName}`);
    return currencyFromPattern;
  }

  console.warn(`Client ${client.name} has unknown culture info "${client.cultureInfoName}", using default currency:`, DEFAULT_CURRENCY);
  return DEFAULT_CURRENCY;
};

export const getCurrencyByCultureInfoName = (cultureInfoName: string): string => {
  return CULTURE_TO_CURRENCY_MAP[cultureInfoName] || DEFAULT_CURRENCY;
};

/**
 * Try to extract currency from culture pattern (e.g., 'en-US' -> 'USD')
 * @param cultureCode Culture code like 'en-US', 'fr-CA', etc.
 * @returns Currency code if pattern matches, otherwise null
 */
const extractCurrencyFromCulturePattern = (cultureCode: string): string | null => {
  if (!cultureCode || !cultureCode.includes('-')) {
    return null;
  }

  // Extract country code (part after the dash)
  const parts = cultureCode.split('-');
  const lastPart = parts[parts.length - 1];

  if (!lastPart) {
    return null;
  }

  const countryCode = lastPart.toUpperCase();

  // Common country code to currency mappings
  const countryToCurrencyMap: Record<string, string> = {
    US: 'USD',
    CA: 'CAD',
    GB: 'GBP',
    AU: 'AUD',
    NZ: 'NZD',
    JP: 'JPY',
    KR: 'KRW',
    CN: 'CNY',
    IN: 'INR',
    BR: 'BRL',
    MX: 'MXN',
    RU: 'RUB',
    ZA: 'ZAR',
    CH: 'CHF', // Switzerland
    SE: 'SEK',
    NO: 'NOK',
    DK: 'DKK',
    PL: 'PLN',
    CZ: 'CZK',
    HU: 'HUF',
    TR: 'TRY',
    IL: 'ILS',
    TH: 'THB',
    VN: 'VND',
    ID: 'IDR',
    MY: 'MYR',
    PH: 'PHP',
    SG: 'SGD', // Singapore
    HK: 'HKD', // Hong Kong
  };

  return countryToCurrencyMap[countryCode] || null;
};

/**
 * Get currency symbol for display purposes
 * @param currencyCode Currency code like 'USD', 'EUR', etc.
 * @returns Currency symbol like '$', '€', etc.
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  try {
    // Use Intl.NumberFormat to get the currency symbol
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    // Extract just the symbol by formatting 0 and removing the number
    return formatter
      .format(0)
      .replace(/[\d\s]/g, '')
      .trim();
  } catch (error) {
    console.warn(`Failed to get symbol for currency ${currencyCode}:`, error);
    return currencyCode; // Fallback to currency code
  }
};

/**
 * Format amount with client's currency
 * @param amount Amount to format
 * @param client Client object containing culture information
 * @returns Formatted currency string
 */
export const formatClientCurrency = (amount: number, client?: ClientOption | null): string => {
  const currency = getClientCurrency(client);
  const locale = client?.cultureInfoName || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  } catch (error) {
    console.warn(`Failed to format currency with locale ${locale} and currency ${currency}:`, error);
    // Fallback to USD formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
};

export const formatClientCurrencyByCultureInfoName = (amount: number, cultureInfoName: string): string => {
  const currency = getCurrencyByCultureInfoName(cultureInfoName);
  const locale = cultureInfoName || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  } catch (error) {
    console.warn(`Failed to format currency with locale ${locale} and currency ${currency}:`, error);
    // Fallback to USD formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
};

/**
 * Check if a currency code is supported
 * @param currencyCode Currency code to check
 * @returns true if currency is supported, false otherwise
 */
export const isSupportedCurrency = (currencyCode: string): boolean => {
  try {
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(0);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get all supported currencies from the mapping
 * @returns Array of currency codes
 */
export const getSupportedCurrencies = (): string[] => {
  return Array.from(new Set(Object.values(CULTURE_TO_CURRENCY_MAP)));
};

/**
 * Get debugging information about client currency resolution
 * @param client Client object
 * @returns Object with currency resolution details
 */
export const getClientCurrencyDebugInfo = (client?: ClientOption | null) => {
  if (!client) {
    return {
      hasClient: false,
      hasCultureInfo: false,
      cultureInfoName: null,
      resolvedCurrency: DEFAULT_CURRENCY,
      resolutionMethod: 'default-no-client',
    };
  }

  const hasCultureInfo = !!client.cultureInfoName;
  const directMapping = client.cultureInfoName ? CULTURE_TO_CURRENCY_MAP[client.cultureInfoName] : null;
  const patternMapping = client.cultureInfoName ? extractCurrencyFromCulturePattern(client.cultureInfoName) : null;

  let resolvedCurrency = DEFAULT_CURRENCY;
  let resolutionMethod = 'default';

  if (directMapping) {
    resolvedCurrency = directMapping;
    resolutionMethod = 'direct-mapping';
  } else if (patternMapping) {
    resolvedCurrency = patternMapping;
    resolutionMethod = 'pattern-mapping';
  }

  return {
    hasClient: true,
    hasCultureInfo,
    cultureInfoName: client.cultureInfoName,
    resolvedCurrency,
    resolutionMethod,
    directMapping,
    patternMapping,
  };
};
